import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';

import { apiClient } from '../lib/api.js';
import { useAuth } from '../lib/auth-context.js';
import type { TalentSummary, TalentCategory } from '@arx-marketplace/shared/talents';
import type { TulpaSummary } from '@arx-marketplace/shared/tulpas';
import { cn, formatVersion } from '../lib/utils.js';

export function PublishPage(): JSX.Element {
  const { user, loading } = useAuth();
  const [talents, setTalents] = useState<TalentSummary[]>([]);
  const [tulpas, setTulpas] = useState<TulpaSummary[]>([]);
  const [form, setForm] = useState<'none' | 'talent' | 'tulpa'>('none');
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    Promise.all([apiClient.listTalents({ per_page: 100 }), apiClient.listTulpas({ per_page: 100 })])
      .then(([tl, tp]) => {
        setTalents(tl.items.filter((t) => t.owner_id === user.id));
        setTulpas(tp.items.filter((t) => t.owner_id === user.id));
      })
      .catch((e: Error) => setErr(e.message));
  }, [user]);

  if (loading) return <div className="container py-12 text-muted-foreground">Loading…</div>;
  if (!user) {
    return (
      <div className="container py-12">
        <p>Please sign in to publish.</p>
        <a
          href={apiClient.loginGithubUrl()}
          className="mt-4 inline-block underline underline-offset-4"
        >
          Sign in with GitHub
        </a>
      </div>
    );
  }

  return (
    <section className="container py-10 flex-1 max-w-4xl">
      <header className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">Your publications</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setForm((v) => (v === 'talent' ? 'none' : 'talent'))}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-2 rounded-md border',
              'hover:bg-secondary transition-colors',
            )}
          >
            <Plus className="h-4 w-4" />
            Talent
          </button>
          <button
            onClick={() => setForm((v) => (v === 'tulpa' ? 'none' : 'tulpa'))}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-2 rounded-md',
              'bg-primary text-primary-foreground hover:bg-primary/90 transition-colors',
            )}
          >
            <Plus className="h-4 w-4" />
            Tulpa
          </button>
        </div>
      </header>

      {err && <div className="mt-4 text-destructive text-sm">{err}</div>}
      {form === 'talent' && <CreateTalentForm onCreated={() => location.reload()} />}
      {form === 'tulpa' && <CreateTulpaForm onCreated={() => location.reload()} />}

      <h2 className="mt-8 text-lg font-semibold">Tulpas</h2>
      <ul className="mt-3 divide-y rounded-lg border">
        {tulpas.map((t) => (
          <li key={t.id} className="p-4 flex items-center justify-between">
            <div>
              <Link to={`/publish/tulpa/${t.slug}`} className="font-semibold hover:underline">
                {t.name}
              </Link>
              <div className="text-xs text-muted-foreground mt-1">
                {t.engines.join(', ') || 'no engine'} ·{' '}
                {t.latest_version ? formatVersion(t.latest_version).label : 'no versions yet'}
              </div>
            </div>
            <Link to={`/u/${t.slug}`} className="text-sm underline-offset-4 hover:underline">
              public page →
            </Link>
          </li>
        ))}
        {tulpas.length === 0 && (
          <li className="p-4 text-sm text-muted-foreground">No tulpas yet.</li>
        )}
      </ul>

      <h2 className="mt-8 text-lg font-semibold">Talents</h2>
      <ul className="mt-3 divide-y rounded-lg border">
        {talents.map((t) => (
          <li key={t.id} className="p-4 flex items-center justify-between">
            <div>
              <Link to={`/publish/${t.slug}`} className="font-semibold hover:underline">
                {t.name}
              </Link>
              <div className="text-xs text-muted-foreground mt-1">
                {t.category} · {t.latest_version ? formatVersion(t.latest_version).label : 'no versions yet'}
              </div>
            </div>
            <Link to={`/t/${t.slug}`} className="text-sm underline-offset-4 hover:underline">
              public page →
            </Link>
          </li>
        ))}
        {talents.length === 0 && (
          <li className="p-4 text-sm text-muted-foreground">No talents yet.</li>
        )}
      </ul>
    </section>
  );
}

function CreateTulpaForm({ onCreated }: { onCreated: () => void }): JSX.Element {
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [summary, setSummary] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      await apiClient.createTulpa({ slug, name, summary });
      onCreated();
    } catch (e2) {
      setErr((e2 as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit} className="mt-6 rounded-lg border p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Slug (lowercase, hyphens)">
          <input
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="pp-writer"
            required
            pattern="[a-z0-9][a-z0-9-]*"
            className="w-full px-3 py-2 rounded-md border bg-background"
          />
        </Field>
        <Field label="Name">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="w-full px-3 py-2 rounded-md border bg-background"
          />
        </Field>
        <Field label="Summary">
          <input
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="One-line description"
            className="w-full px-3 py-2 rounded-md border bg-background"
          />
        </Field>
      </div>
      {err && <div className="text-destructive text-sm">{err}</div>}
      <p className="text-xs text-muted-foreground">
        After creating, upload a <code>.tulpa</code> bundle on the tulpa's edit page. Engine, model,
        and bundled talents are read from its manifest.
      </p>
      <button
        type="submit"
        disabled={submitting}
        className={cn(
          'px-3 py-2 rounded-md bg-primary text-primary-foreground',
          'disabled:opacity-50 hover:bg-primary/90 transition-colors',
        )}
      >
        {submitting ? 'Creating…' : 'Create tulpa'}
      </button>
    </form>
  );
}

function CreateTalentForm({ onCreated }: { onCreated: () => void }): JSX.Element {
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [summary, setSummary] = useState('');
  const [category, setCategory] = useState<TalentCategory>('skill');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      await apiClient.createTalent({ slug, name, summary, category });
      onCreated();
    } catch (e2) {
      setErr((e2 as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit} className="mt-6 rounded-lg border p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Slug (lowercase, hyphens)">
          <input
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="ux-psychologist"
            required
            pattern="[a-z0-9][a-z0-9-]*"
            className="w-full px-3 py-2 rounded-md border bg-background"
          />
        </Field>
        <Field label="Name">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="w-full px-3 py-2 rounded-md border bg-background"
          />
        </Field>
        <Field label="Category">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as TalentCategory)}
            className="w-full px-3 py-2 rounded-md border bg-background"
          >
            <option value="skill">Skill</option>
            <option value="knowledge">Knowledge</option>
            <option value="toolchain">Toolchain</option>
            <option value="policy">Policy</option>
          </select>
        </Field>
        <Field label="Summary">
          <input
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="One-line description"
            className="w-full px-3 py-2 rounded-md border bg-background"
          />
        </Field>
      </div>
      {err && <div className="text-destructive text-sm">{err}</div>}
      <button
        type="submit"
        disabled={submitting}
        className={cn(
          'px-3 py-2 rounded-md bg-primary text-primary-foreground',
          'disabled:opacity-50 hover:bg-primary/90 transition-colors',
        )}
      >
        {submitting ? 'Creating…' : 'Create talent'}
      </button>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block text-sm">
      <span className="text-muted-foreground">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
